import React, { useEffect, useMemo, useState } from 'react';
import './SignalFlyConfigurationModal.css';
import { API_KEY, BASE_URL, ensureArray } from "./config";

const REQUIRED_PROCESSING_KEYS = new Set([
  'ALLOWED_TIME_FRAMES', 'ALLOWED_EXCHANGE_MARKET_TYPES', 'PROCESSING_FALLBACK_DELAY_BY_TIMEFRAME',
]);

const processingHint = key => ({
  ALLOWED_TIME_FRAMES: 'Canonical minutes, for example: 5,15,30,60,240,480,720,1440',
  ALLOWED_EXCHANGE_MARKET_TYPES: 'Exchange/market pairs, for example: BITGET:PERPS,BITGET:SPOT',
  PROCESSING_FALLBACK_DELAY_BY_TIMEFRAME: 'ISO-8601 delays, for example: 5:PT4M,15:PT4M',
}[String(key || '').toUpperCase()]);

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}

function SignalFlyConfigurationModal() {
  const [configList, setConfigList] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [newConfig, setNewConfig] = useState({ configKey: '', configValue: '' });
  const [filters, setFilters] = useState({ configKey: '', configValue: '' });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${BASE_URL}/api/bot/ui/post/getSignalFlyConfiguration`, {
      headers: {
        "X-SIGNALFLY-KEY": API_KEY
      }
    })
      .then(parseResponse)
      .then((data) => {
        const normalizedConfigs = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn('Unexpected SignalFly configuration payload shape', data);
        }
        setConfigList(normalizedConfigs);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, []);

  const filteredConfigList = useMemo(() => {
    return configList.filter((item) =>
      ['configKey', 'configValue'].every((field) => {
        const filterText = (filters[field] || '').trim().toLowerCase();
        if (!filterText) return true;
        return String(item[field] ?? '').toLowerCase().includes(filterText);
      })
    );
  }, [configList, filters]);

  const clearFilters = () => setFilters({ configKey: '', configValue: '' });

  const handleChange = (e, id, field) => {
    const updated = configList.map((item) =>
      item.id === id ? { ...item, [field]: e.target.value } : item
    );
    setConfigList(updated);
  };

  const handleSave = (config) => {
    if (processingHint(config.configKey) && !window.confirm(`Save impactful processing configuration ${config.configKey}?`)) return;
    setError(null);
    fetch(`${BASE_URL}/api/bot/ui/post/saveSignalFlyConfiguration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "X-SIGNALFLY-KEY": API_KEY
      },
      body: JSON.stringify(config),
    })
      .then(parseResponse)
      .then((saved) => {
        const updatedList = configList.map((item) =>
          item.id === saved.id ? saved : item
        );
        setConfigList(updatedList);
        setEditItem(null);
      })
      .catch((err) => setError(err.message));
  };

  const handleDelete = (id) => {
    const selected = configList.find(item => item.id === id);
    if (REQUIRED_PROCESSING_KEYS.has(String(selected?.configKey || '').toUpperCase())) {
      setError(`${selected.configKey} is required and cannot be deleted.`);
      return;
    }
    fetch(`${BASE_URL}/api/bot/ui/post/deleteSignalFlyConfiguration?id=${id}`, {
      method: 'DELETE',
      headers: {
        "X-SIGNALFLY-KEY": API_KEY
      }
    })
      .then(parseResponse)
      .then(() => {
        setConfigList(configList.filter((item) => item.id !== id));
      })
      .catch((err) => setError(err.message));
  };

  const handleAdd = () => {
    fetch(`${BASE_URL}/api/bot/ui/post/saveSignalFlyConfiguration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "X-SIGNALFLY-KEY": API_KEY
      },
      body: JSON.stringify(newConfig),
    })
      .then(parseResponse)
      .then((created) => {
        setConfigList([...configList, created]);
        setNewConfig({ configKey: '', configValue: '' });
      })
      .catch((err) => setError(err.message));
  };

  return (
    <section className="sf-config-page">
      <div className="sf-config-header">
        <h2 className="sf-config-title">Signal Fly Configurations</h2>
        <button className="sf-config-clear-btn" onClick={clearFilters}>
          <span className="sf-config-clear-btn__icon" aria-hidden="true" />
          Clear Filters
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="sf-config-table-wrapper">
        <table className="sf-config-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Key</th>
              <th>Value</th>
              <th>Actions</th>
            </tr>
            <tr className="sf-config-filter-row">
              <th></th>
              <th>
                <input
                  value={filters.configKey}
                  onChange={(event) => setFilters({ ...filters, configKey: event.target.value })}
                  placeholder="Filter key"
                />
              </th>
              <th>
                <input
                  value={filters.configValue}
                  onChange={(event) => setFilters({ ...filters, configValue: event.target.value })}
                  placeholder="Filter value"
                />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan="4" className="table-status-cell">Loading...</td>
              </tr>
            )}
            {!isLoading && filteredConfigList.length === 0 && (
              <tr>
                <td colSpan="4" className="table-status-cell">No configurations found.</td>
              </tr>
            )}
            {!isLoading && filteredConfigList.map((config, index) => (
              <tr key={config.id}>
                <td>{index + 1}</td>
                <td data-label="Key">
                  {editItem === config.id ? (
                    <input
                      className="enterprise-input"
                      value={config.configKey}
                      onChange={(e) => handleChange(e, config.id, 'configKey')}
                    />
                  ) : (
                    config.configKey
                  )}
                </td>
                <td data-label="Value" className="value-cell">
                  {editItem === config.id ? (
                    <div><input className="enterprise-input" value={config.configValue}
                      onChange={(e) => handleChange(e, config.id, 'configValue')} />
                      {processingHint(config.configKey) && <small className="sf-config-hint">{processingHint(config.configKey)}</small>}</div>
                  ) : (
                    config.configValue
                  )}
                </td>
                <td data-label="Actions" className="actions-cell">
                  {editItem === config.id ? (
                    <>
                      <button className="btn save" onClick={() => handleSave(config)}>Save</button>
                      <button className="btn cancel" onClick={() => setEditItem(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="btn edit" onClick={() => setEditItem(config.id)}>Edit</button>
                      <button className="btn delete" disabled={REQUIRED_PROCESSING_KEYS.has(String(config.configKey).toUpperCase())}
                        onClick={() => handleDelete(config.id)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            <tr className="add-row">
              <td>+</td>
              <td>
                <input
                  className="enterprise-input"
                  placeholder="New Key"
                  value={newConfig.configKey}
                  onChange={(e) => setNewConfig({ ...newConfig, configKey: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="enterprise-input"
                  placeholder="New Value"
                  value={newConfig.configValue}
                  onChange={(e) => setNewConfig({ ...newConfig, configValue: e.target.value })}
                />
              </td>
              <td className="actions-cell">
                <button className="btn add" onClick={handleAdd}>Add</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="sf-config-record-count">
        Showing {filteredConfigList.length} of {configList.length} rows
      </div>
    </section>
  );
}

export default SignalFlyConfigurationModal;
